import React from "react";
import {
  FiGlobe,
  FiHash,
  FiInfo,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiSave,
  FiServer,
} from "react-icons/fi";
import { SlEarphonesAlt } from "react-icons/sl";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import {
  SERVICE_CATEGORY_SUGGESTIONS,
  SERVICE_FORM_TYPE_OPTIONS,
  SERVICE_ICON_SUGGESTIONS,
  SERVICES_MUNICIPAUX_FORM_HELPERS,
} from "../data/servicesMunicipauxAdminData";

export default function ServicesMunicipauxFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
}) {
  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier le service municipal" : "Nouveau service municipal"}
      subtitle={
        isEdit
          ? "Mettez à jour les informations du service puis enregistrez."
          : "Créez une fiche pratique pour le site public de la commune."
      }
      icon={SlEarphonesAlt}
      size="full"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Identité du service
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField
                label="Nom du service"
                required
                error={fieldErrors.nom_service}
              >
                <input
                  value={form.nom_service}
                  onChange={(event) =>
                    onChange("nom_service", event.target.value)
                  }
                  placeholder="Ex : Sport et jeunesse"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField
                label="Slug"
                required
                error={fieldErrors.slug_service}
                help={`URL publique : /services/${form.slug_service || "..."}`}
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiHash className="text-slate-400" />

                  <input
                    value={form.slug_service}
                    onChange={(event) =>
                      onChange("slug_service", event.target.value)
                    }
                    placeholder="sport-et-jeunesse"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Catégorie">
                <input
                  value={form.categorie_service}
                  onChange={(event) =>
                    onChange("categorie_service", event.target.value)
                  }
                  placeholder="Animation, Accueil, Solidarité..."
                  list="service-form-categories"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="service-form-categories">
                  {SERVICE_CATEGORY_SUGGESTIONS.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </AdminField>

              <AdminField label="Type">
                <select
                  value={form.type_service || ""}
                  onChange={(event) =>
                    onChange("type_service", event.target.value)
                  }
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                >
                  {SERVICE_FORM_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AdminField>

              <AdminField
                label="Parent ID"
                help="Optionnel. Permet de rattacher ce service à un autre."
              >
                <input
                  value={form.parent_id}
                  onChange={(event) =>
                    onChange("parent_id", event.target.value)
                  }
                  placeholder="Ex : 12"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Icône front">
                <input
                  value={form.icone_service}
                  onChange={(event) =>
                    onChange("icone_service", event.target.value)
                  }
                  placeholder="Ex : FaUsers"
                  list="service-icon-suggestions"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="service-icon-suggestions">
                  {SERVICE_ICON_SUGGESTIONS.map((icon) => (
                    <option key={icon} value={icon} />
                  ))}
                </datalist>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Présentation
            </h3>

            <div className="mt-4 space-y-4">
              <AdminField label="Accroche">
                <input
                  value={form.accroche_service}
                  onChange={(event) =>
                    onChange("accroche_service", event.target.value)
                  }
                  placeholder="Phrase courte affichée en introduction"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Description">
                <AdminRichTextEditor
                  value={form.description_service}
                  onChange={(html) => onChange("description_service", html)}
                  placeholder="Présentez le service, ses missions, ses modalités d’accès..."
                />
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Coordonnées
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <AdminField label="Téléphone">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiPhone className="text-slate-400" />

                  <input
                    value={form.tel_service}
                    onChange={(event) =>
                      onChange("tel_service", event.target.value)
                    }
                    placeholder="05 xx xx xx xx"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Téléphone secondaire">
                <input
                  value={form.tel_secondaire}
                  onChange={(event) =>
                    onChange("tel_secondaire", event.target.value)
                  }
                  placeholder="Optionnel"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField
                label="Email(s)"
                error={fieldErrors.mail_service}
                help="Plusieurs emails possibles, séparés par ; ou ,"
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMail className="text-slate-400" />

                  <input
                    value={form.mail_service}
                    onChange={(event) =>
                      onChange("mail_service", event.target.value)
                    }
                    placeholder="contact@mairie.fr"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Site web">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiGlobe className="text-slate-400" />

                  <input
                    value={form.website_service}
                    onChange={(event) =>
                      onChange("website_service", event.target.value)
                    }
                    placeholder="https://..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Autre lien utile">
                <input
                  value={form.autre_url_service}
                  onChange={(event) =>
                    onChange("autre_url_service", event.target.value)
                  }
                  placeholder="Portail, billetterie, formulaire..."
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Adresse et localisation
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField label="Adresse">
                <textarea
                  value={form.adresse_service}
                  onChange={(event) =>
                    onChange("adresse_service", event.target.value)
                  }
                  rows={3}
                  placeholder="Adresse complète"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <div className="grid gap-4 sm:grid-cols-2">
                <AdminField label="Code postal">
                  <input
                    value={form.cp_service}
                    onChange={(event) =>
                      onChange("cp_service", event.target.value)
                    }
                    placeholder="33520"
                    className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />
                </AdminField>

                <AdminField label="Ville">
                  <input
                    value={form.ville_service}
                    onChange={(event) =>
                      onChange("ville_service", event.target.value)
                    }
                    placeholder="Bruges"
                    className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />
                </AdminField>
              </div>

              <AdminField
                label="Coordonnées GPS"
                error={fieldErrors.gps_service}
                help="Format attendu : latitude, longitude. Exemple : 44.8742, -0.6413"
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMapPin className="text-slate-400" />

                  <input
                    value={form.gps_service}
                    onChange={(event) =>
                      onChange("gps_service", event.target.value)
                    }
                    placeholder="44.8742, -0.6413"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Horaires et informations utiles
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField label="Horaires">
                <textarea
                  value={form.horaires_service}
                  onChange={(event) =>
                    onChange("horaires_service", event.target.value)
                  }
                  rows={5}
                  placeholder="Ex : Lun-Jeu 8h30-12h30 / 13h30-17h"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Informations complémentaires">
                <textarea
                  value={form.infos_complementaires}
                  onChange={(event) =>
                    onChange("infos_complementaires", event.target.value)
                  }
                  rows={5}
                  placeholder="Modalités d’accueil, rendez-vous, pièces à fournir..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_active", !form.is_active)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_active ? "Service actif" : "Service inactif"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_active
                    ? "La fiche est activée en administration."
                    : "La fiche est désactivée."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_active ? "bg-emerald-500" : "bg-rose-500"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_active ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>

            <button
              type="button"
              onClick={() => onChange("is_public", !form.is_public)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_public
                  ? "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
                  : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_public ? "Visible public" : "Masqué du public"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_public
                    ? "La fiche peut apparaître sur le site."
                    : "La fiche reste uniquement dans l’administration."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_public ? "bg-sky-500" : "bg-slate-400"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_public ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Ordre d’affichage
            </p>

            <AdminField label="Position" className="mt-3">
              <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                <FiInfo className="text-slate-400" />

                <input
                  type="number"
                  value={form.ordre_affichage}
                  onChange={(event) =>
                    onChange("ordre_affichage", event.target.value)
                  }
                  className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                />
              </div>
            </AdminField>
          </div>

          <div className="rounded-[1.7rem] border border-cyan-100 bg-cyan-50 p-4 dark:border-cyan-500/20 dark:bg-cyan-500/10">
            <p className="text-sm font-bold text-cyan-900 dark:text-cyan-200">
              Aide à la saisie
            </p>

            <ul className="mt-3 space-y-2">
              {SERVICES_MUNICIPAUX_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-cyan-800 dark:text-cyan-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}